// src/services/api.js

import axios from 'axios';
import { getAuth } from 'firebase/auth';

// ✅ Ensure API works both locally & on GCP
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 
  (window.location.hostname === 'localhost' 
    ? 'http://localhost:8080/api' 
    : 'https://ops-console.nezo-app.com/api');

console.log("🔍 Using API Base URL:", API_BASE_URL);


// ===== AUTH & REGION HELPERS =====

export const getAuthToken = async () => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    if (user) {
      return await user.getIdToken();
    }
    return null;
  } catch (error) {
    console.error("❌ Error getting auth token:", error);
    return null;
  }
};

export const getCurrentRegion = () => {
  return localStorage.getItem('selectedRegion') || 'vquest';
};

export const getUserHeaders = () => {
  const auth = getAuth();
  const user = auth.currentUser;
  
  return {
    'x-user-id': user?.uid || 'unknown',
    'x-user-email': user?.email || 'unknown',
  };
};


// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
});

// ===== GLOBAL MODAL STATE =====
let showModalCallback = null;

export const setShowModalCallback = (callback) => {
  showModalCallback = callback;
};

// ===== REQUEST INTERCEPTOR ===== // ← ADD THIS ENTIRE BLOCK
// 1. Attach bypass token
// 2. Add auth token
// 3. Add region header
api.interceptors.request.use(
  async (config) => {
    try {
      // 1. Attach bypass token if available
      const bypassToken = sessionStorage.getItem('rateLimitBypass');
      if (bypassToken) {
        config.headers['X-Rate-Limit-Bypass'] = bypassToken;
        console.log('🔓 Using bypass token');
      }

      // 2. Add Firebase authentication token
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (user) {
        const token = await user.getIdToken();
        config.headers.Authorization = `Bearer ${token}`;
        console.log('🔐 Added auth token to request:', config.url);
      } else {
        console.warn('⚠️ No authenticated user for request:', config.url);
      }
      
      // 3. Add region header
      const selectedRegion = localStorage.getItem('selectedRegion') || 'vquest';
      config.headers['x-region'] = selectedRegion;
      console.log('🌏 Adding region header:', selectedRegion);
      
    } catch (error) {
      console.error('❌ Error in request interceptor:', error);
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ===== RESPONSE INTERCEPTOR =====
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 - redirect to login
    if (error.response?.status === 401) {
      console.error('🚫 Unauthorized - redirecting to login');
      // Don't redirect if already on login page
      if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
        window.location.href = '/login';
      }
    }
    
    // Handle 429 - show bypass modal for admins
    if (error.response?.status === 429) {
      console.log('🚨 Rate limit hit:', error.response.data);
      
      // ✅ Only show modal if user is logged in AND is admin
      const isLoggedIn = window.location.pathname !== '/login' && window.location.pathname !== '/';
      
      if (error.response.data.canBypass && isLoggedIn) {
        console.log('🔓 Admin detected - showing bypass modal');
        
        if (showModalCallback) {
          showModalCallback(true);
        } else {
          console.error('❌ Modal callback not registered!');
        }
      } else if (!isLoggedIn) {
        console.log('⏭️ Skipping modal - user on login page');
      } else {
        console.log('⏱️ Non-admin user must wait');
      }
    }
    
    return Promise.reject(error);
  }
);


// ===== REGION MANAGEMENT =====

export const setRegion = (regionId) => {
  localStorage.setItem('selectedRegion', regionId);
  console.log("🌏 Region set to:", regionId);
};

export const getRegion = () => {
  return localStorage.getItem('selectedRegion') || 'vquest';
};

export const clearRegion = () => {
  localStorage.removeItem('selectedRegion');
};

// 🌏 Fetch available regions (PUBLIC endpoint - no auth required)
export const fetchRegions = async () => {
  try {
    // Use regular axios for public endpoint to bypass auth interceptor
    const response = await axios.get(`${API_BASE_URL}/regions`);
    console.log("✅ Fetched Regions:", response.data);
    return response.data || [];
  } catch (error) {
    console.error("❌ Error fetching regions:", error.response?.data || error);
    // Fallback regions
    return [
      { id: 'vquest', name: 'New Zealand', country_code: 'NZ' },
      { id: 'vquest-au-1', name: 'Australia', country_code: 'AU' },
      { id: 'vquest-uk-1', name: 'United Kingdom', country_code: 'UK' },
      { id: 'vquest-us-1', name: 'USA/Canada', country_code: 'US' },
      { id: 'vquest-midrise', name: 'Mid Rise', country_code: 'NZ' }
    ];
  }
};

// ===== DASHBOARD & MATERIALS =====

export const debugApiBaseUrl = () => {
  console.log("✅ API Base URL:", API_BASE_URL);
};

export const fetchTotalMaterials = async () => {
  try {
    const response = await api.get("/dashboard/total-materials");
    if (response.data && typeof response.data === "object" && "total" in response.data) {
      console.log("📌 Total Materials API Response:", response.data);
      return response.data.total;
    } else {
      console.error("❌ Unexpected API Response (Not JSON):", response);
      return 0;
    }
  } catch (error) {
    console.error("❌ Error fetching total materials:", error.response?.data || error);
    return 0;
  }
};

export const fetchMaterials = async (region = 'nz') => {
  try {
    const response = await api.get(`/materialeditor/materials`, {
      params: { region }
    });
    return { data: response.data };
  } catch (error) {
    console.error('Error fetching materials:', error);
    throw error;
  }
};

// ===== CATEGORIES =====

export const fetchCategories = async () => {
  try {
    const response = await api.get("/categories");
    console.log("✅ Fetched Categories Data:", response.data);
    return response.data || [];
  } catch (error) {
    console.error("❌ Error fetching categories:", error.response?.data || error);
    return [];
  }
};

export const fetchNewCategories = async () => {
  try {
    const response = await api.get("/managecategory");
    return response.data;
  } catch (error) {
    console.error("❌ Error fetching categories:", error.response?.data || error);
    return [];
  }
};

export const fetchMaterialRequestCategories = () => api.get('/material-requests/categories');

export const addNewCategory = async (data) => {
  try {
    const response = await api.post("/managecategory", data);
    return response.data;
  } catch (error) {
    console.error("❌ Error adding category:", error.response?.data || error);
    throw error;
  }
};

export const updateCategory = async (id, data) => {
  try {
    const response = await api.put(`/managecategory/${id}`, data);
    return response.data;
  } catch (error) {
    console.error("❌ Error updating category:", error.response?.data || error);
    throw error;
  }
};

export const deleteCategory = async (id) => {
  try {
    await api.delete(`/managecategory/${id}`);
  } catch (error) {
    console.error("❌ Error deleting category:", error.response?.data || error);
    throw error;
  }
};

// Hide / show a category without touching its name, priority or section pointer.
// Used to retire shadow building-element EPD roots after the Nezo EPD re-home.
export const toggleCategoryVisibility = async (id, isHidden) => {
  try {
    const response = await api.put(`/managecategory/${id}/visibility`, { isHidden });
    return response.data;
  } catch (error) {
    console.error("❌ Error toggling category visibility:", error.response?.data || error);
    throw error;
  }
};

export const updateCategoryPriority = async (updatedCategories) => {
  try {
    const requestData = { updates: updatedCategories };
    console.log("📤 Sending priority updates:", JSON.stringify(requestData, null, 2));
    
    const response = await api.put("/managecategory/update-priority", requestData, {
      headers: { 
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      validateStatus: (status) => true
    });

    if (response.status !== 200) {
      console.error("❌ Server Error:", response.data);
      throw new Error(response.data?.error || "Unknown error updating priorities");
    }

    console.log("✅ Updated Priorities:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Error updating priorities:", error.response?.data || error.message);
    throw error;
  }
};

// ===== PRODUCTS =====

export const addNewProduct = async (productData) => {
  try {
    const response = await api.post('/newproduct/add-product', productData);
    return response.data;
  } catch (error) {
    console.error("❌ Error adding product:", error);
    throw error;
  }
};

// ===== BUILD-UP & BREAKDOWN =====

export const fetchBuildUpByCategory = (quantityCatId, userEmail = '', userRole = 'user') =>
  api.get(`/build-up/${encodeURIComponent(quantityCatId)}`, {
    headers: {
      'x-user-email': userEmail,
      'x-user-role': userRole,
    }
  });

export const fetchBreakdownByQuantityUnit = (quantityUnitId, filter = null) => {
  let url = `/breakdown/${quantityUnitId}`;
  if (filter) {
    url += `?filter=${filter.join(',')}`;
  }
  return api.get(url);
};

export const fetchBreakdownData = async (quantityUnitId) => {
  return api.get(`/breakdown/${quantityUnitId}`);
};

// ===== SUPPLY RATES =====

export const fetchSupplyRates = () => api.get('/supplyrates');

// ===== COST MANAGEMENT =====

export const fetchCostRates = async () => {
  return await api.get(`/editcost`);
};

export const fetchEditCost = () => api.get('/editcost');

export const updateCostRate = (data) => api.put('/editcost', data);

export const deleteCostRate = (id) => api.delete(`/editcost/${id}`);

export const fetchCostCategories = () => api.get('/newcost/categories');

export const fetchNewCostQuantityTypes = () => api.get('/newcost/quantity-types');

export const addNewCost = (data) => api.post('/newcost/add-cost', data);

// ===== WEIGHT & CO2 DATA =====

export const fetchWeightData = () => api.get('/weight');

export const fetchCo2Data = () => api.get('/co2');

export const addNewCo2Weight = (data) => api.post('/newco2weight', data);

// ── Athena (USA-Canada / vquest-us-1) OmniClass mapping helpers ─────────────────────────
// All are hard-gated to vquest-us-1 on the backend (routes/athena.js requireAthenaRegion);
// the x-region header is auto-injected by the request interceptor above.
export const fetchAthenaOc21Elements = async () => (await api.get('/athena/oc21-elements')).data;
export const fetchAthenaOc22WorkResults = async () => (await api.get('/athena/oc22-work-results')).data;
export const searchAthenaFlows = async (search = '', group = '') =>
  (await api.get('/athena/flows', { params: { search, group } })).data;
export const fetchAthenaFlowGroups = async () =>
  (await api.get('/athena/flow-groups')).data;
export const fetchAthenaLineMappings = async (quantityUnitId) =>
  (await api.get(`/athena/line-mappings/${quantityUnitId}`)).data;
export const saveAthenaQuOc21 = (data) => api.post('/athena/qu-oc21', data);
export const saveAthenaMurOc23 = (data) => api.post('/athena/mur-oc23', data);
export const saveAthenaQumurfOc22 = (data) => api.post('/athena/qumurf-oc22', data);

export const getCo2WeightById = async (id) => {
  try {
    const response = await api.get(`/editco2weight/${id}`);
    return response.data;
  } catch (error) {
    console.error("❌ Error fetching CO2/Weight item by ID:", error);
    throw error;
  }
};

export const updateCo2Weight = async (id, data) => {
  try {
    console.log("📤 API - Sending CO2 update request:", { id, data });
    const response = await api.put(`/editco2weight`, data);
    console.log("✅ API Response for Edit Co2:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ API Error updating CO2, Biogenic, and Weight data:", error.response?.data || error);
    throw error;
  }
};

export const deleteCo2Weight = async (id) => {
  try {
    const response = await api.delete(`/editco2weight/${id}`);
    return response.data;
  } catch (error) {
    console.error("❌ Error deleting CO2, Biogenic, and Weight data:", error.response?.data || error);
    throw error;
  }
};

export const fetchEditCo2Weight = async () => {
  try {
    const response = await api.get('/editco2weight');
    console.log("🔍 API - Fetch response:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Error fetching CO2, Biogenic, and Weight data:", error);
    throw error;
  }
};

// ===== NEW MATERIAL & BREAKDOWN =====

export const fetchMaterialsWithBreakdown = async () => {
  try {
    const response = await api.get('/newbreakdown/materials-with-breakdown');
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching materials with breakdown data:', error);
    return [];
  }
};

export const fetchMurCategories = async (type) => {
  const response = await api.get('/lookup/mur-categories', {
    params: { type }
  });
  return response.data;
};

export const fetchMeasurements = () => api.get('/measurements');

export const fetchTestData = async () => {
  try {
    const response = await api.get('/newmaterial');
    console.log('🔍 fetchTestData raw response:', response);
    console.log('🔍 fetchTestData response.data:', response.data);
    console.log('🔍 Is array?:', Array.isArray(response.data));
    
    // ✅ FIX: Return the data directly
    const materials = response.data;
    
    // Ensure it's an array
    if (!Array.isArray(materials)) {
      console.error('❌ Response is not an array:', materials);
      return [];
    }
    
    console.log('✅ Returning materials:', materials.length);
    return materials;
  } catch (error) {
    console.error('❌ Error fetching test data:', error);
    return [];
  }
};


export const fetchNewMaterialMeasurements = async () => {
  try {
    const response = await api.get('/newmaterial/measurements');
    console.log('🔍 fetchNewMaterialMeasurements response:', response.data);
    return response.data || [];
  } catch (error) {
    console.error('❌ Error fetching measurements:', error);
    return [];
  }
};

export const fetchQuantityTypes = async () => {
  try {
    const response = await api.get('/newmaterial/quantity-types');
    console.log('🔍 fetchQuantityTypes response:', response.data);
    // ✅ FIX: Return the array directly, not the response object
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('❌ Error fetching quantity types:', error);
    return [];
  }
};

export const fetchNewMaterialCategories = async () => {
  try {
    const response = await api.get('/newmaterial/categories');
    console.log('🔍 fetchNewMaterialCategories response:', response.data);
    // ✅ FIX: Return the array directly, not the response object
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('❌ Error fetching categories:', error);
    return [];
  }
};


export const addTestData = async (testData) => {
  try {
    const response = await api.post('/newmaterial', testData);
    return response.data;
  } catch (error) {
    console.error('❌ Error adding test data:', error);
    throw error;
  }
};

export const updateTestData = async (id, testData) => {
  try {
    const response = await api.put(`/newmaterial/${id}`, testData);
    return response.data;
  } catch (error) {
    console.error('❌ Error updating test data:', error);
    throw error;
  }
};

export const deleteTestData = async (id) => {
  try {
    const response = await api.delete(`/newmaterial/${id}`);
    return response.data;
  } catch (error) {
    console.error('❌ Error deleting test data:', error);
    throw error;
  }
};


// ===== MATERIAL EDITOR =====

export const fetchMaterialLineItems = (materialId) => 
  api.get(`/materialeditor/material-line-items/${materialId}`);

export const fetchMeasurementUnitRateCategories = () => api.get('/materialeditor/categories');

export const addLineItems = (lineItems) => api.post('/materialeditor/line-items', { lineItems });

export const fetchMeasurementCategories = () => api.get('/materialeditor/measurement-categories');

export const updateMaterialData = (data) => api.post('/materialeditor/update-material', data);

export const fetchFilteredMeasurementCategories = (measurementId) =>
  api.get(`/materialeditor/measurement-categories/${measurementId}`);

export const deleteMaterial = async (materialId) => {
  try {
    const response = await api.delete(`/materialeditor/delete-material/${materialId}`);
    return response.data;
  } catch (error) {
    console.error('❌ Error deleting material:', error);
    throw error;
  }
};

// ===== LOGS =====

export const fetchLogs = () => api.get('/logs');

// Company-scoped activity logs (company admins only). The companyAuth middleware
// resolves the company from x-user-email, which the interceptor does not add.
export const fetchCompanyLogs = () => {
  const u = getAuth().currentUser;
  return api.get('/logs/company', {
    headers: { 'x-user-email': u?.email || '' },
  });
};

export const logAction = async (userId, userEmail, actionType, actionDetails) => {
  try {
    const response = await api.post(`/logs`, {
      userId,
      userEmail,
      actionType,
      actionDetails,
    });
    console.log('Action logged successfully:', response.data);
  } catch (error) {
    console.error('Error logging action:', error);
  }
};

// ===== MISC =====

export const fetchCategoryByMeasurementUnitName = async (measurementUnitName) => {
  try {
    const response = await api.get(`/newbreakdown/category/${encodeURIComponent(measurementUnitName)}`);
    console.log('API response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error in API request:', error);
    throw error;
  }
};

export const saveQuantityCalculatorData = (data) => api.post('/quantity-calculator', data);

export const fetchQuantityCalculatorData = async (materialId) => {
  try {
    const response = await api.get(`/line-items/${materialId}`);
    
    console.log('🔍 fetchQuantityCalculatorData raw response:', response);
    console.log('🔍 response.data:', response.data);
    console.log('🔍 response.data.data:', response.data.data);
    
    return response; // ✅ Return the full axios response
  } catch (error) {
    console.error('Error fetching quantity calculator data:', error);
    throw error;
  }
};

export const createBuildUp = (categoryId, buildUpData) =>
  api.post(`/categories/${categoryId}/build-up`, buildUpData);

// ===== EC3 DATA =====

export const fetchEC3Data = async () => {
  try {
    const response = await api.get('/ec3');
    return response.data;
  } catch (error) {
    console.error("❌ Error fetching EC3 data:", error.response?.data || error);
    return [];
  }
};

// ===== COMPOSITE REQUESTS =====

export const submitCompositeRequest = async (payload) => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    
    const response = await api.post('/composite-requests', payload, {
      headers: {
        'X-User-ID': user?.uid || 'unknown',
        'X-User-Email': user?.email || 'unknown',
        'X-User-Role': (user?.role || 'user').toLowerCase(),
      }
    });
    return response.data;
  } catch (error) {
    console.error("❌ Error submitting composite request:", error);
    throw error;
  }
};

export const fetchPendingCompositeRequests = async () => {
  try {
    const response = await api.get('/composite-requests/pending', {
      headers: { 'X-User-Role': 'admin' }
    });
    return response.data;
  } catch (error) {
    console.error("❌ Error fetching pending composite requests:", error);
    throw error;
  }
};

export const approveCompositeRequest = async (requestId, notes='') => {
  try {
    const response = await api.post(`/composite-requests/${requestId}/approve`, {
      reviewNotes: notes
    }, {
      headers: { 'X-User-Role': 'admin' }
    });
    return response.data;
  } catch (error) {
    console.error("❌ Error approving composite request:", error);
    throw error;
  }
};

export const rejectCompositeRequest = async (requestId, notes) => {
  try {
    const response = await api.post(`/composite-requests/${requestId}/reject`, {
      reviewNotes: notes
    }, {
      headers: { 'X-User-Role': 'admin' }
    });
    return response.data;
  } catch (error) {
    console.error("❌ Error rejecting composite request:", error);
    throw error;
  }
};

// ===== R-VALUES =====

export const fetchRValue = async (quantityUnitId) => {
  try {
    const response = await api.get(`/r-values/${quantityUnitId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching R-Value:', error);
    throw error;
  }
};

export const saveRValue = async ({ quantityUnitId, rValue, calculatorRows, category }) => {  // ← ADD category parameter
  try {
    console.log('🔍 API saveRValue called with:', {
      quantityUnitId,
      rValue,
      calculatorRows,
      category  // ← ADD THIS to logs
    });

    const response = await api.post(`/r-values/${quantityUnitId}`, {
      rValue,
      calculatorRows: calculatorRows || [],
      category: category || '',  // ← ADD THIS to request body
    });

    console.log('✅ API saveRValue success:', response.data);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('❌ API saveRValue error:', error);
    return { success: false, error: error.message };
  }
};


export const uploadRValueDocument = async (rValueId, file) => {
  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await api.post(`/r-values/${rValueId}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading R-Value document:', error);
    throw error;
  }
};

export const deleteRValue = async (quantityUnitId) => {
  try {
    const response = await api.delete(`/r-values/${quantityUnitId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting R-Value:', error);
    throw error;
  }
};

// 🆕 Fetch documents for an R-Value
export const fetchRValueDocuments = async (rValueId) => {
  try {
    const response = await api.get(`/r-values/${rValueId}/documents`);
    return response.data;
  } catch (error) {
    console.error('Error fetching R-Value documents:', error);
    throw error;
  }
};

// 🆕 Delete an R-Value document
export const deleteRValueDocument = async (rValueId, documentId) => {
  try {
    const response = await api.delete(`/r-values/${rValueId}/documents/${documentId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting R-Value document:', error);
    throw error;
  }
};

// 🆕 Refresh signed URL for an expired document
export const refreshDocumentUrl = async (rValueId, documentId) => {
  try {
    const response = await api.get(`/r-values/${rValueId}/documents/${documentId}/refresh-url`);
    return response.data;
  } catch (error) {
    console.error('Error refreshing document URL:', error);
    throw error;
  }
};



// ===== UNIFIED AUTH HEADERS (Legacy Support) =====

export const getAuthHeaders = async () => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken();
      return { Authorization: `Bearer ${token}` };
    }
  } catch (error) {
    console.error("❌ Error getting auth headers:", error);
  }
  return {};
};

// ===== COST EDITOR =====

// ✅ Cost Editor APIs
export const fetchCostMaterials = async (regionId) => {
  const response = await api.get('/cost-editor/cost-materials', {
    headers: { 'X-Region': regionId },
  });
  return response.data;
};

export const fetchCostMaterialBreakdown = async (measurementUnitRateId, regionId) => {
  const response = await api.get(`/cost-editor/cost-materials/${measurementUnitRateId}/breakdown`, {
    headers: { 'X-Region': regionId },
  });
  return response.data;
};

export const fetchCostMaterialLineItems = async (measurementUnitRateId, regionId) => {
  const response = await api.get(`/cost-editor/cost-materials/${measurementUnitRateId}/line-items`, {
    headers: { 'X-Region': regionId },
  });
  return response.data;
};

export const updateCostMaterialName = async (measurementUnitRateId, name, regionId) => {
  const response = await api.put(
    `/cost-editor/cost-materials/${measurementUnitRateId}/name`,
    { name },
    { headers: { 'X-Region': regionId } }
  );
  return response.data;
};

export const deleteCostMaterial = async (measurementUnitRateId, regionId) => {
  const response = await api.delete(`/cost-editor/cost-materials/${measurementUnitRateId}`, {
    headers: { 'X-Region': regionId },
  });
  return response.data;
};

export const saveCostMaterialBreakdown = async (measurementUnitRateId, lineItems, regionId) => {
  const response = await api.post(
    `/cost-editor/cost-materials/${measurementUnitRateId}/breakdown`,
    { lineItems },
    { headers: { 'X-Region': regionId } }
  );
  return response.data;
};

// CATEGORIES IMAGE GENERATION //

export const getCategoryImage = async (categoryId) => {
  try {
    const response = await api.get(`/category-images/${categoryId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching category image:', error);
    return null;
  }
};

export const batchGenerateCategoryImages = async (limit = 5) => {
  try {
    const response = await api.post('/category-images/batch-generate', { limit });
    return response.data;
  } catch (error) {
    console.error('Error batch generating images:', error);
    throw error;
  }
};

// ===== BUNNINGS API =====

export const fetchBunningsItems = async (itemNumbers) => {
  try {
    const response = await api.post('/bunnings/import', { itemNumbers });
    return response.data;
  } catch (error) {
    console.error("❌ Error fetching Bunnings items:", error.response?.data || error);
    throw error;
  }
};

export const fetchBunningsSyncedItems = async () => {
  try {
    const response = await api.get('/bunnings/synced-items');
    return response.data;
  } catch (error) {
    console.error("❌ Error fetching synced Bunnings items:", error.response?.data || error);
    throw error;
  }
};

export const fetchBunningsCategories = async () => {
  try {
    const response = await api.get('/bunnings/categories');
    return response.data;
  } catch (error) {
    console.error("❌ Error fetching Bunnings categories:", error.response?.data || error);
    throw error;
  }
};

export const fetchBunningsQuantityTypes = async () => {
  try {
    const response = await api.get('/bunnings/quantity-types');
    return response.data;
  } catch (error) {
    console.error("❌ Error fetching Bunnings quantity types:", error.response?.data || error);
    throw error;
  }
};

export const assignBunningsCategory = async (measurementUnitRateId, categoriesId, quantityType) => {
  try {
    const response = await api.post('/bunnings/assign', {
      measurementUnitRateId,
      categoriesId,
      quantityType
    });
    return response.data;
  } catch (error) {
    console.error("❌ Error assigning Bunnings category:", error.response?.data || error);
    throw error;
  }
};

export const syncBunningsPrices = async () => {
  try {
    const response = await api.post('/bunnings/sync-now');
    return response.data;
  } catch (error) {
    console.error("❌ Error syncing Bunnings prices:", error.response?.data || error);
    throw error;
  }
};

export const fetchBunningsConfig = async () => {
  try {
    const response = await api.get('/bunnings/config');
    return response.data;
  } catch (error) {
    console.error("❌ Error fetching Bunnings config:", error.response?.data || error);
    throw error;
  }
};

export const updateBunningsConfig = async (config) => {
  try {
    const response = await api.post('/bunnings/config', config);
    return response.data;
  } catch (error) {
    console.error("❌ Error updating Bunnings config:", error.response?.data || error);
    throw error;
  }
};




export default api;
